<template>
  <HpSidebar
    :id="id"
    icon="heart-filled"
    title="Your Saved Plans"
    class="[&_.sidebar-title_svg]:text-sky-800"
  >
    <section
      v-if="displayedPlans.length === 0"
      class="m-5 flex h-fit min-h-[95%] flex-col items-center justify-center rounded-3xl bg-gray-200 p-5"
    >
      <div class="grid max-w-80 place-items-center gap-5">
        <h3 class="max-w-56 text-center text-3xl">
          <HpIcon name="heart-filled" class="mx-auto mb-1 size-9 text-sky-800" />
          Save Plans to Compare Later
        </h3>
        <p class="text-balance text-center leading-relaxed text-gray-900">
          Tap the heart on any plan to save it here. Then compare your favorites side-by-side to
          find the best fit.
        </p>
      </div>
    </section>

    <ul v-else class="mx-auto my-3 max-w-[22rem] space-y-5">
      <li
        v-if="showPushToSave"
        class="mx-auto my-7 grid max-w-[22rem] place-items-center gap-4 rounded-3xl bg-yellow-300 px-5 pb-8 pt-4 text-center"
      >
        <h3 class="max-w-64 text-2xl font-bold leading-tight">
          <span class="block text-3xl">😮</span>
          Don't Lose Your Saved Plans
        </h3>

        <p class="max-w-64 text-lg leading-relaxed">
          On your next visit, your saved plans won't be here anymore ☹️ ...unless you
          <strong>save your progress</strong>. It's quick with no passwords to remember!
        </p>

        <HpButton variant="secondary" class="mt-2 max-w-56" @click="handleSaveMyProgress">
          Save My Progress
        </HpButton>
      </li>
      <li
        v-for="quote in displayedPlans"
        :key="quote.medicareId"
        class="subtle-shadow-card grid overflow-hidden rounded-quote border border-border"
      >
        <header class="flex items-center justify-between gap-3 bg-gray-200 px-6 py-3">
          <div>
            <h3 class="text-2xl">
              {{ quote.details.brandName }}
            </h3>
            <p class="text-base leading-tight text-gray-900">
              {{ formatPlanName(quote.name) }}
            </p>
          </div>
          <QuotePlanScoreV2
            v-if="!isGhostCardActive && quote.recommendation?.exists"
            :score="quote.score"
            size="small"
          />
        </header>

        <div class="mb-4 mt-8 grid place-items-center gap-2">
          <HpButton variant="secondary" class="max-w-56" @click="viewPlanDetails(quote.medicareId)">
            View Details
          </HpButton>

          <HpButton
            @click="removePlan(quote.medicareId, quote.details.year)"
            variant="minimal"
            icon="delete"
            class="!text-gray-800 hover:!text-danger [&_svg]:mr-0.5 [&_svg]:text-gray-800 [&_svg]:hover:text-danger"
          >
            Remove
          </HpButton>
        </div>
      </li>
    </ul>

    <template #footer>
      <div class="mx-auto flex w-full max-w-96 flex-col gap-y-2">
        <HpButton
          :disabled="displayedPlans.length < 2"
          variant="primary"
          class="w-full"
          @click="comparePlans"
        >
          Compare my saved plans
        </HpButton>
        <HpButton variant="secondary" class="w-full" @click="hide(id)"> Close </HpButton>
      </div>
    </template>
  </HpSidebar>
  <PxPushToSaveDialog
    :id="pushToSaveId"
    scenario="saved-plan"
    presentation="sidebar"
    source="saved-plan-sidebar"
  />
</template>

<script setup lang="ts">
  import { ProfileStore } from '~/stores/profile'
  import { EngineStore } from '~/stores/engine'
  import PxPushToSaveDialog from '~/components/pushToSave/PushToSaveDialog.vue'
  import QuotePlanScoreV2 from '~/components/quote/QuotePlanScoreV2.vue'
  import { WaypointResult } from '~/generated/api-clients-generated'

  const props = defineProps<{
    id: string
  }>()

  const { show, hide } = DialogController.use()
  const profile = ProfileStore.use()
  const session = SessionStore.use()
  const engine = EngineStore.use()
  const { push } = AppRouter.use()
  const { flag } = FeatureFlags.use()
  const { isAuthenticated } = Auth.use()
  const { track } = Analytics.use()
  const { isGhostCardActive } = GhostCard.use()

  const isPushToSave = computed(() => flag('push-to-save-saved-plan').value)
  const showPushToSave = computed(() => !isAuthenticated.value && isPushToSave.value)
  const pushToSaveId = computed(() => `${props.id}-push-to-save`)

  const displayedPlans = computed(() =>
    (profile.savedPlans[session.planYear!] ?? [])
      .map((x) => engine.quotesCatalog[x])
      .filter((x) => x != null)
  )

  const handleSaveMyProgress = () => {
    show(pushToSaveId.value)
    track('PushToSaveShown', { scenario: 'savedPlanSidebar' })
  }

  const formatPlanName = (name: string) => {
    return name.replace(/\(.*\)/, '').trim()
  }

  const viewPlanDetails = (medicareId: string) => {
    push(`/details/${medicareId}`)
    hide(props.id)
  }

  const removePlan = (medicareId: string, year: number) => {
    profile.removeSavedPlan(year, medicareId)
  }

  const comparePlans = async () => {
    session.comparedPlans = displayedPlans.value.map((x) => x.medicareId)
    const { engage } = WaypointAutoPilot.use(WaypointResult.GoTo_Compare)
    await engage()
    hide(props.id)
  }
</script>

<style scoped></style>
